/**
 * Excel / CSV から商品カタログ取込 (Phase J 重層化版)。
 *
 * フロー:
 *  1. ファイル選択 (xlsx / xls / csv)
 *  2. ブラウザでパース → 列マッピング + プレビュー表示
 *  3. カテゴリ + バージョン名 + ラベルを入力
 *     - 既存カテゴリから選択 or 新規入力
 *     - バージョンは「ユーザー命名 or 年_季節_種別」自動補完
 *     - 「適用中」チェックすると即時 active 化
 *  4. 「取込実行」で:
 *     a) upsertProductCatalog でカタログメタ作成
 *     b) bulkImportToCatalog で items 投入 (mode='replace' = 新規カタログなので安全)
 *
 * ★ このダイアログは常に「新規カタログを作る」。
 *   既存カタログを最新価格表で上書きしたい場合は、商品マスター右ペインの
 *   「CSV 一括上書き」(CatalogCsvOverwriteDialog / mode='sync') を使うこと。
 *
 * ★ 列の解決は catalogSheetParser (ヘッダー名ベース) に一任している。
 *   2026-08-18 の「製品カテゴリ」列追加のような差分で静かに 1 列ずれないようにするため、
 *   ここに固定インデックスを書き戻さないこと。
 */
import { ArrowRight, FileSpreadsheet, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import type { ParsedSheet } from '../catalogSheetParser';
import {
  BULK_IMPORT_MAX_ROWS,
  CatalogColumnMap,
  CatalogRowsPreview,
  CatalogSheetSummaryBar,
  readCatalogSheetFile,
  summarize,
  toImportItems,
} from '../CatalogSheetPreview';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import {
  useBulkImportToCatalog,
  useProductCatalogs,
  useUpsertProductCatalog,
} from '@/hooks/useProductCatalogs';


interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** 従来の取込が全行に焼いていた既定値。編集可能な入力欄の初期値として残す。 */
const DEFAULT_SUPPLIER_NAME = 'TimeWaver GmbH';

function defaultVersionName(): string {
  const y = new Date().getFullYear();
  const m = String(new Date().getMonth() + 1).padStart(2, '0');
  return `${y}_${m}_general`;
}

export function ImportProductsDialog({ open, onOpenChange }: Props) {
  const { data: catalogs } = useProductCatalogs();
  const upsertCatalog = useUpsertProductCatalog();
  const bulkImport = useBulkImportToCatalog();

  const [step, setStep] = useState<'file' | 'meta'>('file');
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState<ParsedSheet | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // メタ入力
  const [category, setCategory] = useState('');
  const [version, setVersion] = useState('');
  const [label, setLabel] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [notes, setNotes] = useState('');
  const [supplierName, setSupplierName] = useState(DEFAULT_SUPPLIER_NAME);

  const existingCategories = useMemo(() => {
    const set = new Set<string>();
    for (const c of catalogs ?? []) set.add(c.category);
    return Array.from(set).sort();
  }, [catalogs]);

  const summary = useMemo(
    () => (parsed ? summarize(parsed.rows) : null),
    [parsed],
  );

  // ダイアログを開いた直後に初期化
  useEffect(() => {
    if (!open) return;
    setStep('file');
    setFile(null);
    setParsed(null);
    setCategory('');
    setVersion(defaultVersionName());
    setLabel('');
    setIsActive(true);
    setNotes('');
    setSupplierName(DEFAULT_SUPPLIER_NAME);
  }, [open]);

  // 仕入先の入力を変えたら、シートに仕入先列が無い行へ反映し直す
  const importItems = useMemo(() => {
    if (!parsed) return [];
    // 新規カタログなので温存すべき既存値は無いが、シートに列が無ければキーを落とす挙動は共通化しておく
    const items = toImportItems(parsed.rows, parsed.columns.productCategory != null);
    const fallback = supplierName.trim() || null;
    if (parsed.columns.supplierName != null) return items;
    return items.map((it) => ({ ...it, supplierName: fallback }));
  }, [parsed, supplierName]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // 同じファイルを選び直せるようにリセット
    if (!f) return;
    setFile(f);
    setParsed(null);
    setParsing(true);
    try {
      const result = await readCatalogSheetFile(f);
      setParsed(result);
      if (result.rows.length === 0) {
        toast.error('取込可能な行が見つかりませんでした');
      } else {
        toast.success(`${result.rows.length} 件検出`);
        setStep('meta');
      }
    } catch (err) {
      toast.error(`パース失敗: ${(err as Error).message}`);
    } finally {
      setParsing(false);
    }
  };

  const handleSubmit = async () => {
    if (!parsed || importItems.length === 0 || !category.trim()) {
      toast.error('カテゴリと有効な行が必要です');
      return;
    }
    if (importItems.length > BULK_IMPORT_MAX_ROWS) {
      toast.error(
        `一度に取込めるのは ${BULK_IMPORT_MAX_ROWS} 件までです (${importItems.length} 件)`,
      );
      return;
    }
    try {
      // 1. カタログメタ作成
      const r1 = await upsertCatalog.mutateAsync({
        category: category.trim(),
        version: version.trim() || defaultVersionName(),
        label: label.trim() || undefined,
        isActive,
        notes: notes.trim() || null,
      });
      // 2. items 一括投入 (新規カタログなので replace で安全)
      const r2 = await bulkImport.mutateAsync({
        catalogId: r1.id,
        mode: 'replace',
        items: importItems,
      });
      toast.success(
        `カタログ作成完了: ${r2.total} 件投入${isActive ? ' (適用中)' : ''}`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(`取込失敗: ${(err as Error).message}`);
    }
  };

  const submitting = upsertCatalog.isPending || bulkImport.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            商品カタログ取込
            <span className="text-xs text-[var(--text-muted)] ml-2 font-normal">
              ({step === 'file' ? '1/2: ファイル選択' : '2/2: メタ入力 + 確認'})
            </span>
          </DialogTitle>
          <DialogDescription>
            「価格表（社内用）」フォーマットの Excel (.xlsx) または CSV
            ファイルを選択し、カテゴリ・バージョン名を指定して
            <strong>新規カタログ</strong>を作成します。
            既存カタログの価格を差し替えたい場合は、内訳商品の「CSV 一括上書き」を使ってください。
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: ファイル選択 */}
        {step === 'file' && (
          <div className="rounded-md border-2 border-dashed border-[var(--border)] bg-[var(--bg-elevated)] p-8 text-center">
            <FileSpreadsheet className="size-10 text-[var(--text-muted)] mx-auto mb-3" />
            <p className="text-sm text-[var(--text-secondary)] mb-3">
              {file
                ? file.name
                : 'Excel (.xlsx / .xls) または CSV (.csv) を選択してください'}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,text/csv"
              onChange={handleFileChange}
              className="hidden"
            />
            <Button
              variant="primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={parsing}
            >
              <Upload className="size-3.5" />
              {parsing ? 'パース中…' : 'ファイルを選択'}
            </Button>
            <p className="text-[10px] text-[var(--text-muted)] mt-3">
              列はヘッダー名で自動判定します（製品名 / 製品カテゴリ / 仕入価格 (JPY) /
              税抜価格 (JPY) / 税込価格 (JPY) / 備考）。
              「製品カテゴリ」列が無い旧フォーマットもそのまま取込めます。
            </p>
          </div>
        )}

        {/* Step 2: メタ入力 + プレビュー */}
        {step === 'meta' && parsed && summary && (
          <div className="space-y-4">
            {/* カタログメタ入力 */}
            <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider">
                カタログ情報
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">ブランド (メーカー) *</Label>
                  <input
                    list="seed-categories"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="例: TimeWaver / Energy 4 Life"
                    className="w-full rounded border border-[var(--border)] bg-[var(--bg-input)] text-sm px-3 py-2"
                  />
                  <datalist id="seed-categories">
                    {existingCategories.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                  {existingCategories.length > 0 && (
                    <p className="text-[10px] text-[var(--text-muted)] mt-1">
                      既存: {existingCategories.join(', ')}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs">バージョン名 *</Label>
                  <Input
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    placeholder="例: 2025_spring_general"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">表示ラベル (任意)</Label>
                  <Input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="例: 2025年春季 通常価格"
                  />
                </div>
                <div>
                  <Label className="text-xs">仕入先名 (全行に適用)</Label>
                  <Input
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                    placeholder="例: TimeWaver GmbH"
                    disabled={parsed.columns.supplierName != null}
                  />
                  <p className="text-[10px] text-[var(--text-muted)] mt-1">
                    {parsed.columns.supplierName != null
                      ? 'シートに仕入先列があるため、そちらの値を使います。'
                      : 'TimeWaver 以外のブランドを取込むときは必ず変更してください。'}
                  </p>
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                <span>
                  このカタログを <strong>適用中</strong> にする
                  (同カテゴリの他バージョンは自動解除)
                </span>
              </label>
            </div>

            {/* 列マッピング + プレビュー */}
            <CatalogColumnMap parsed={parsed} />
            <CatalogSheetSummaryBar
              summary={summary}
              onReset={() => {
                setStep('file');
                setParsed(null);
                setFile(null);
              }}
            />
            <CatalogRowsPreview rows={parsed.rows} maxHeightClass="max-h-[360px]" />
          </div>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            キャンセル
          </Button>
          {step === 'meta' && summary && summary.valid > 0 && (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || !category.trim()}
            >
              {submitting ? '取込中…' : `カタログ作成 (${summary.valid} 件)`}
              <ArrowRight className="size-3.5" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
